import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, Grid, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useFormik } from 'formik'
import * as yup from 'yup'

import UserAPI from '../../api/orbitV1/UserAPI'
import BreadcrumbsMUI from '../../common/AbstractBreadcrumbs/BreadcrumbsMUI'
import PageLayout from '../../common/Layout/Page/PageLayout'
import { ColorBoxContent } from '../../common/ui/colorBox'
import OrbitSession from '../../hooks/useCookies'
import useQueryParams from '../../hooks/useQueryParams'

import { AccountForm, DeleteDialog } from './components'

export default function ViewAndUpdateAccount() {
  const navigate = useNavigate()
  const userId = useQueryParams().get('user')
  const orgId = OrbitSession.get('orgId')
  const [updateSelected, setUpdateSelected] = useState(false)
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [currentName, setCurrentName] = useState('')
  const queryClient = useQueryClient()

  /* Formik variables */
  const validationSchema = yup.object({
    contactName: yup.string().required('Contact Name is required'),
    contactNumber: yup
      .string()
      .matches(/^([689])\d{7}$/, 'Invalid Contact Number')
      .required('Contact Number is required'),
    email: yup
      .string()
      .email('Enter a valid email')
      .required('Email is required'),
    role: yup.string()
  })

  const editAccFormik = useFormik({
    initialValues: {
      contactName: '',
      contactNumber: '',
      email: '',
      role: ''
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  const baseRoute = '/hq/manage-accounts'

  const getUserDetails = async () => {
    try {
      const token = OrbitSession.get('faberToken')
      const userResponse = await UserAPI.getUserByQuery(
        { orgId, user: userId },
        token
      )
      if (userResponse && userResponse.data[0]) {
        const user = userResponse.data[0]
        editAccFormik.setFieldValue('contactName', user.name)
        editAccFormik.setFieldValue('email', user.email)
        editAccFormik.setFieldValue('contactNumber', user.contactNumber)
        editAccFormik.setFieldValue('role', user.role)
        setCurrentName(user.name)
      }
    } catch (err) {
      toast.error('Error loading user details!')
    }
  }

  useEffect(() => {
    const getData = async () => {
      await getUserDetails()
    }

    getData()
  }, [])

  const handleUpdateBtnClicked = () => {
    setUpdateSelected(!updateSelected)
  }

  const handleCancelBtnClicked = () => {
    setUpdateSelected(!updateSelected)
    location.href = ''
  }
  // Pending completion of components
  const handleFormSubmit = async (e) => {
    e.preventDefault()
    // compose submission object, awaiting API

    const { contactName, contactNumber, email, role } = editAccFormik.values

    const requestBody = {
      name: contactName,
      email,
      contactNumber,
      role,
      orgId,
      userId
    }

    await UserAPI.updateUser(requestBody)
    setUpdateSelected(!updateSelected)

    toast.success('User Account successfully updated!', {
      autoClose: 3000,
      hideProgressBar: false
    })

    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'getOrgDetails' &&
        query.queryKey[1] === 'accountManagement'
    })

    setTimeout(() => {
      navigate(baseRoute)
    }, 3000)
  }

  const handleDeleteAlert = () => {
    setDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    setDialogOpen(false)

    try {
      const deleteUserRes = await UserAPI.deleteUser(userId)
      if (deleteUserRes && deleteUserRes.data?.message) {
        toast.success('User Account successfully deleted.', {
          autoClose: 3000,
          hideProgressBar: false
        })
        setTimeout(() => {
          navigate(baseRoute)
        }, 3000)
      } else {
        toast.error(
          'There was an issue deleting the User Account, please try again later.'
        )
      }
    } catch (err) {
      throw new Error(err)
    }
  }

  return (
    <PageLayout header={updateSelected ? 'Update Account' : 'View Profile'}>
      <Grid container direction='column' spacing={2}>
        <Grid item>
          <ColorBoxContent variant='white'>
            <BreadcrumbsMUI
              crumbs={[
                {
                  route: baseRoute,
                  name: 'User Accounts'
                },
                {
                  route: '',
                  name: 'View Profile'
                }
              ]}
            />
          </ColorBoxContent>
        </Grid>

        <Grid item>
          <ColorBoxContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography variant='h4'>{currentName}</Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'right',
                  width: '100%',
                  marginRight: 16
                }}
              >
                {!updateSelected ? (
                  <Button
                    variant='secondary'
                    value='Delete'
                    onClick={handleDeleteAlert}
                    data-cy='deleteAccBtn'
                  >
                    Delete Account
                  </Button>
                ) : (
                  ''
                )}
                {!updateSelected ? (
                  <Button
                    variant='filled'
                    value='Update'
                    onClick={handleUpdateBtnClicked}
                    data-cy='updateAccBtn'
                  >
                    Update Account
                  </Button>
                ) : (
                  ''
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <AccountForm
                formik={editAccFormik}
                updateSelected={Boolean(updateSelected)}
              />

              {updateSelected ? (
                <Box sx={{ display: 'flex', flexDirection: 'flex-end' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                    <Button
                      variant='secondary'
                      onClick={handleCancelBtnClicked}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='filled'
                      disabled={
                        !editAccFormik.values.contactName ||
                        !editAccFormik.values.contactNumber ||
                        !editAccFormik.values.email ||
                        editAccFormik.errors.email ||
                        editAccFormik.errors.contactNumber
                      }
                      onClick={handleFormSubmit}
                    >
                      Confirm
                    </Button>
                  </Box>
                </Box>
              ) : (
                ''
              )}
            </Box>
          </ColorBoxContent>
        </Grid>

        <DeleteDialog
          isDialogOpen={isDialogOpen}
          setDialogOpen={setDialogOpen}
          handleConfirmDelete={handleConfirmDelete}
        />
      </Grid>
    </PageLayout>
  )
}
